const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Dispara la descarga de un archivo ya armado en memoria.
 *
 * `revokeObjectURL` va después del `click()` y no en un `setTimeout`: el navegador ya tomó
 * el blob de forma síncrona al hacer clic, y dejar la URL viva retiene el archivo entero en
 * memoria. Con el reporte HTML de un mes eso son decenas de MB colgados hasta recargar.
 */
function descargarBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export function descargarXlsx(buffer: ArrayBuffer, fileName: string): void {
  descargarBlob(new Blob([buffer], { type: XLSX_MIME }), fileName);
}

export function descargarHtml(html: string, fileName: string): void {
  descargarBlob(new Blob([html], { type: 'text/html;charset=utf-8' }), fileName);
}

/**
 * Abre un HTML ya armado en el dialogo de impresion del navegador, que es de donde sale el PDF.
 *
 * **Por que un iframe y no una ventana nueva:** `window.open` lo bloquea el navegador cuando la
 * llamada no cuelga directamente de un clic --y aca cuelga de un `await` que puede tardar minutos
 * bajando fotos--, asi que el usuario apretaria el boton y no pasaria nada. El iframe no se
 * bloquea. Va oculto porque el documento no se mira aca: se mira en la vista previa de impresion.
 *
 * **Por que un blob y no `srcdoc`:** el reporte de un mes pesa decenas de MB y `srcdoc` mete todo
 * el documento en un atributo HTML, que es justo la forma mas caros de pasarlo.
 *
 * El nombre que propone el dialogo sale del `<title>` del documento, no de aca: por eso el
 * generador pone la campana en el titulo.
 *
 * Devuelve una promesa que resuelve cuando el dialogo ya se abrio (o cuando se agoto la espera de
 * carga), para que quien llame pueda apagar su "cargando" en el momento correcto.
 */
export function imprimirHtml(html: string): Promise<void> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
    const marco = document.createElement('iframe');
    // El marco tiene TAMANO REAL, aunque esté fuera de la pantalla.
    //
    // Con `width: 0; height: 0` --como estaba-- las fotos salian EN BLANCO en el PDF: van con
    // `loading="lazy"`, y una imagen diferida solo se carga cuando entra en el viewport. En un
    // marco de 0x0 ninguna entra nunca, asi que al imprimir el navegador componia los huecos.
    // El tamano de aca no cambia como se pagina --de eso se encarga `@page` en la hoja de
    // impresion-- pero le da al documento un viewport donde las imagenes existen.
    marco.style.position = 'fixed';
    marco.style.left = '-20000px';
    marco.style.top = '0';
    marco.style.width = '1024px';
    marco.style.height = '1400px';
    marco.style.border = '0';
    // Sin esto el navegador no deja llamar a `print()` sobre el contenido del marco.
    marco.setAttribute('aria-hidden', 'true');

    let cerrado = false;
    /**
     * El marco NO se quita al terminar de imprimir: mientras el dialogo esta abierto el documento
     * tiene que seguir vivo, y no hay un evento fiable de "ya cerro el dialogo" en todos los
     * navegadores. Se limpia cuando el foco vuelve a la pagina, y como red de seguridad a los dos
     * minutos: dejar decenas de MB colgados hasta recargar es justo lo que `descargarBlob` evita.
     */
    const limpiar = (): void => {
      if (cerrado) return;
      cerrado = true;
      window.removeEventListener('focus', limpiar);
      URL.revokeObjectURL(url);
      marco.remove();
    };

    /**
     * Espera a que TODAS las imagenes esten cargadas antes de imprimir.
     *
     * No es una precaucion de mas: la evidencia va con `loading="lazy"` --que en el archivo
     * interactivo es correcto, son decenas de MB-- y al imprimir hay que forzar lo contrario. Se
     * les quita el diferido y se espera a que completen; sin esto el dialogo se abre con las fotos
     * a medio cargar y el PDF sale con huecos, que es justo la evidencia que el reporte existe
     * para mostrar.
     *
     * Son `data:` URI, asi que cargan de memoria y no hay red que pueda colgarse. Igual hay tope
     * de espera: mas vale un PDF con una foto menos que un boton que no responde nunca.
     */
    const esperarImagenes = async (doc: Document): Promise<void> => {
      const fotos = [...doc.images];
      for (const img of fotos) img.loading = 'eager';
      await Promise.all(fotos.map((img) => (
        img.complete
          ? Promise.resolve()
          : new Promise<void>((listo) => {
            // `error` tambien resuelve: una foto que no carga no puede bloquear el PDF entero.
            img.addEventListener('load', () => listo(), { once: true });
            img.addEventListener('error', () => listo(), { once: true });
            window.setTimeout(listo, 15_000);
          })
      )));
    };

    let yaImprimio = false;
    marco.onload = () => {
      // `onload` de un iframe puede dispararse mas de una vez (el `about:blank` inicial y despues
      // el blob, segun el navegador). Sin esta guarda el usuario ve DOS dialogos de impresion,
      // uno encima del otro.
      if (yaImprimio) return;
      yaImprimio = true;
      const doc = marco.contentDocument;
      const seguir = (): void => {
        try {
          marco.contentWindow?.focus();
          marco.contentWindow?.print();
        } finally {
          window.addEventListener('focus', limpiar);
          window.setTimeout(limpiar, 120_000);
          resolve();
        }
      };
      if (doc === null) { seguir(); return; }
      void esperarImagenes(doc).then(seguir, seguir);
    };
    marco.src = url;
    document.body.appendChild(marco);
  });
}
